import { useState, useRef, useEffect } from 'react'
import { Filter, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import type { DataTableColumn } from './data-table'

export interface ActiveFilter {
  field: string
  operator: string
  value: string
  label: string
}

interface FilterBarProps<T> {
  columns: DataTableColumn<T>[]
  activeFilters: ActiveFilter[]
  onFiltersChange: (filters: ActiveFilter[]) => void
}

export function FilterBar<T>({
  columns,
  activeFilters,
  onFiltersChange,
}: FilterBarProps<T>) {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filterableColumns = columns.filter((c) => c.meta?.filterType)

  useEffect(() => {
    if (!menuOpen) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setMenuOpen(false)
        setEditingField(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  useEffect(() => {
    if (editingField && inputRef.current) inputRef.current.focus()
  }, [editingField])

  function addFilter(colId: string, value: string) {
    const col = columns.find((c) => c.id === colId)
    if (!col) return
    // Use meta.filterField as the query param key, falling back to column id
    const filterField = col.meta?.filterField ?? colId
    const displayValue = col.meta?.filterOptions?.find((o) => o.value === value)?.label ?? value
    const filter: ActiveFilter = {
      field: filterField,
      operator: 'eq',
      value,
      label: `${col.header}: ${displayValue}`,
    }
    onFiltersChange([...activeFilters.filter((f) => f.field !== filterField), filter])
    setEditingField(null)
    setEditingValue('')
    setMenuOpen(false)
  }

  function removeFilter(field: string) {
    onFiltersChange(activeFilters.filter((f) => f.field !== field))
  }

  if (filterableColumns.length === 0) return null

  return (
    <div className="cv-data-table flex items-center gap-2 flex-wrap">
      {/* Filter button */}
      <div className="relative" ref={ref}>
        <Button
          type="button"
          variant="outline"
          onClick={() => setMenuOpen((o) => !o)}
          className="cv-data-table-compact cursor-pointer rounded-[4px] border-[0.5px] border-solid border-[var(--rule)] px-2.5 py-1 uppercase tracking-[0.1em] text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
        >
          <Filter className="h-3 w-3" />
          {t('common.filter', { defaultValue: 'Filter' })}
        </Button>

        {menuOpen && (
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] shadow-[var(--shadow-float)] py-1">
            {editingField ? (
              <div className="px-3 py-2">
                <p className="cv-data-table-compact mb-2 uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  {columns.find((c) => c.id === editingField)?.header}
                </p>
                {(() => {
                  const col = columns.find((c) => c.id === editingField)
                  const filterType = col?.meta?.filterType

                  if (filterType === 'select' && col?.meta?.filterOptions) {
                    return (
                      <div className="flex flex-col gap-0.5">
                        {col.meta.filterOptions.map((opt) => (
                          <Button
                            type="button"
                            variant="ghost"
                            key={opt.value}
                            onClick={() => addFilter(editingField, opt.value)}
                            className="cv-data-table-small h-auto justify-start rounded-[4px] px-2 py-1.5 text-left text-[var(--ink-2)] transition-all duration-100 hover:bg-[var(--bg-subtle)]"
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    );
                  }

                  return (
                    <Input
                      ref={inputRef}
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && editingValue.trim()) {
                          addFilter(editingField, editingValue.trim())
                        }
                        if (e.key === 'Escape') {
                          setEditingField(null)
                          setEditingValue('')
                        }
                      }}
                      placeholder={t('dataTable.filterPlaceholder')}
                      className="cv-data-table-small w-full rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-2.5 py-1.5 text-[var(--ink)] outline-none"
                    />
                  );
                })()}
              </div>
            ) : (
              filterableColumns.map((col) => (
                <Button
                  type="button"
                  variant="ghost"
                  key={col.id}
                  onClick={() => {
                    setEditingField(col.id)
                    setEditingValue('')
                  }}
                  className="cv-data-table-small h-auto w-full justify-start px-3 py-1.5 text-left text-[var(--ink-2)] transition-all duration-100 hover:bg-[var(--bg-subtle)]"
                >
                  {col.header}
                </Button>
              ))
            )}
          </div>
        )}
      </div>
      {/* Active filter pills */}
      {activeFilters.map((filter) => {
        // Re-resolve label at render time so async-loaded filterOptions
        // replace UUIDs once the data arrives.
        const col = columns.find((c) => c.meta?.filterField === filter.field)
        const displayValue =
          col?.meta?.filterOptions?.find((o) => o.value === filter.value)?.label ?? filter.value
        const resolvedLabel = col ? `${col.header}: ${displayValue}` : filter.label

        return (
          <Badge
            key={filter.field}
            variant="outline"
            className="cv-data-table-compact h-auto gap-1.5 rounded-full border-[0.5px] border-[var(--green)] bg-[var(--green-bg)] px-2.5 py-1 text-[var(--green)]"
          >
            {resolvedLabel}
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => removeFilter(filter.field)}
              className="size-auto rounded-none p-0 text-[var(--green)] hover:bg-transparent hover:opacity-70"
            >
              <X className="h-2.5 w-2.5" />
            </Button>
          </Badge>
        );
      })}
    </div>
  );
}
